import { createFileRoute, useNavigate, useSearch, Link } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Loader2, MailWarning } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import { resolveLoginHint, useBranding, type LoginHintChannel } from '@utils/api'
import { getLoginHintToken } from '@utils/loginHintToken'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { HStack } from '@/components/ui'
import { isValidUsername, normalizeUsername } from '@/validators/username'

type LoginSearch = {
  redirect?: string
  locale?: string
}

// Which account this screen's password belongs to, and so what it asks for and
// what it submits.
//   resolving — a link carried a hint token and the API has not answered yet
//   hint      — the token named an account; password only
//   expired   — the token named nothing; the ordinary form, with a heads-up
//   normal    — no token, or the patient chose a different account
type LoginMode = 'resolving' | 'hint' | 'expired' | 'normal'

// No form is shown while the resolve is in flight, so a request that never
// answers would strand the patient on a spinner with no way to sign in and no
// way to reach password reset. Generous, because the link arrives by text and
// the patient is typically on cellular data: a slow round trip that still
// lands is worth more than the ordinary form, which cannot name their account.
const RESOLVE_TIMEOUT_MS = 15000

// The mask the API returns is drawn with bullets (•••-•••-1234, j•••@x.com),
// which a screen reader reads as punctuation or drops entirely. Recognising the
// account by ear needs the part of it that is actually revealed.
const spokenContact = (masked: string, channel: LoginHintChannel | null) => {
  if (channel === 'email') {
    const at = masked.lastIndexOf('@')
    const start = at > 0 ? masked.slice(0, at).replace(/•/g, '') : ''
    const domain = at > 0 ? masked.slice(at + 1) : ''

    return start && domain ? { kind: 'email' as const, start, domain } : null
  }

  const tail = masked.match(/(\d{2,})$/)

  // Spaced so the digits are read one by one rather than as one large number.
  return tail ? { kind: 'sms' as const, digits: tail[1].split('').join(' ') } : null
}

export const Route = createFileRoute('/secure/login')({
  component: () => (
    <AuthProvider>
      <Login />
    </AuthProvider>
  ),
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    return {
      redirect: search.redirect as string | undefined,
      locale: search.locale as string | undefined,
    }
  },
})

function Login() {
  const navigate = useNavigate()
  const { redirect, locale } = useSearch({ from: '/secure/login' })
  const { branding } = useBranding()
  const { login, isAuthenticated } = useAuth()
  const { t, i18n } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)

  // Read once per mount: the token is module state that the portal clears at
  // the session boundary, and the screen must not change shape underneath a
  // patient who is part-way through typing.
  const [hintToken] = useState(getLoginHintToken)
  const [mode, setMode] = useState<LoginMode>(hintToken ? 'resolving' : 'normal')
  const [maskedContact, setMaskedContact] = useState<string | null>(null)
  const [channel, setChannel] = useState<LoginHintChannel | null>(null)
  const [resolveFellBack, setResolveFellBack] = useState(false)
  const usernameRef = useRef<HTMLInputElement | null>(null)
  const chosenDifferentAccount = useRef(false)

  useEffect(() => {
    if (locale) i18n.changeLanguage(locale)
  }, [i18n, locale])

  const form = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
  })

  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: redirect || '/secure/portal' })
    }
  }, [navigate, isAuthenticated, redirect])

  useEffect(() => {
    if (!hintToken) return

    let current = true

    // Dropping the pending resolve here as well: once the ordinary form stands
    // in, a late answer must not swap the screen under someone already typing.
    const timeout = setTimeout(() => {
      if (!current) return
      current = false
      setResolveFellBack(true)
      setMode('normal')
    }, RESOLVE_TIMEOUT_MS)

    resolveLoginHint(hintToken)
      .then(({ status, body }) => {
        if (!current) return

        if (status === 200 && body?.maskedContact) {
          setMaskedContact(body.maskedContact)
          setChannel(body.channel ?? null)
          setMode('hint')
          return
        }

        // A 4xx is the API saying this token names no account of this clinic —
        // one uniform answer covering expired, revoked and unknown alike.
        // Anything else (server fault, offline, unreadable body) is no evidence
        // about the link, so it falls back to the ordinary screen rather than
        // telling the patient their link is dead.
        if (status >= 400 && status < 500) {
          setMode('expired')
          return
        }

        setResolveFellBack(true)
        setMode('normal')
      })
      .catch(() => {
        if (!current) return

        setResolveFellBack(true)
        setMode('normal')
      })
      .finally(() => clearTimeout(timeout))

    return () => {
      current = false
      clearTimeout(timeout)
    }
  }, [hintToken])

  // The control that leaves hint mode unmounts with it, so focus would land
  // nowhere; it is moved onto the field the patient now has to fill.
  useEffect(() => {
    if (mode === 'normal' && chosenDifferentAccount.current) {
      chosenDifferentAccount.current = false
      usernameRef.current?.focus()
    }
  }, [mode])

  const useDifferentAccount = () => {
    chosenDifferentAccount.current = true
    setMode('normal')
  }

  const onSubmit = async (values: { username: string; password: string }) => {
    try {
      // The token goes up only from the screen that resolved it. An identifier
      // the patient typed is their own choice of account and a token we could
      // not resolve has nothing to add, so neither may carry one.
      const credentials =
        mode === 'hint' && hintToken
          ? { token: hintToken, password: values.password }
          : {
              username: normalizeUsername(values.username),
              password: values.password,
            }

      await login(credentials)
    } catch (error) {
      // Error handling is done in the AuthContext
      console.error('Login failed:', error)
    }
  }

  const contact = maskedContact ? spokenContact(maskedContact, channel) : null
  const contactSpeech =
    contact?.kind === 'email'
      ? t('secure.login.hint.contactSpeechEmail', {
          start: contact.start,
          domain: contact.domain,
        })
      : contact?.kind === 'sms'
        ? t('secure.login.hint.contactSpeechSms', { digits: contact.digits })
        : maskedContact

  // The one announcer for the mode the resolve lands on, including the silent
  // fallback a timed-out or faulted resolve drops the patient on. The notice
  // and the masked contact below are deliberately not live regions themselves:
  // they appear in the same render and would each say the same thing again.
  const announcement =
    mode === 'hint' && maskedContact
      ? t('secure.login.hint.announcement', { contact: contactSpeech })
      : mode === 'expired'
        ? `${t('secure.login.expiredLink.title')} ${t('secure.login.expiredLink.message')}`
        : resolveFellBack
          ? t('secure.login.hint.formReadyAnnouncement')
          : ''

  // Login form is short enough to be vertically centered without scroll issues.
  // Use h-[100svh] with overflow-hidden to prevent any scroll. The link modes
  // add content above the form, so those alone are allowed to grow the page
  // rather than clip the submit button or the way out of a stranger's account.
  const linkMode = mode === 'hint' || mode === 'expired'

  // A captured token makes the password-only screen the expected landing, so
  // the heading commits to it while the resolve runs rather than settling on
  // the generic greeting and then changing under the patient.
  const expectsHint = mode === 'hint' || mode === 'resolving'

  return (
    <div
      className={cn(
        'flex items-center justify-center p-2',
        linkMode ? 'min-h-[100svh] py-6' : 'h-[100svh] overflow-hidden',
      )}
    >
      <div
        className="w-full max-w-sm bg-white p-6 rounded-2xl space-y-4 sm:space-y-6"
        aria-busy={mode === 'resolving'}
      >
        {branding?.logo && (
          <div className="flex justify-center">
            <img
              src={branding.logo}
              alt={`${branding?.name}'s Logo`}
              className="object-contain h-16 sm:h-20 max-w-[200px]"
            />
          </div>
        )}

        <div className="space-y-0 text-center mb-0">
          <h1 className="text-2xl font-semibold tracking-tight">
            {expectsHint ? t('secure.login.hint.title') : t('secure.login.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {expectsHint
              ? t('secure.login.hint.subTitle')
              : t('secure.login.subTitle')}
          </p>
        </div>

        <Separator className="my-3" />

        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {announcement}
        </div>

        {mode === 'resolving' ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="animate-spin text-gray-500" size={28} aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              {t('secure.login.hint.checkingLink')}
            </p>
          </div>
        ) : (
          <>
            {mode === 'hint' && maskedContact && (
              <div className="rounded-md bg-muted px-3 py-2 text-center">
                <p className="text-xs text-gray-700">
                  {channel === 'email'
                    ? t('secure.login.hint.contactLabelEmail')
                    : t('secure.login.hint.contactLabelSms')}
                </p>
                <p className="font-medium tracking-wide">
                  <span aria-hidden="true">{maskedContact}</span>
                  <span className="sr-only">{contactSpeech}</span>
                </p>
              </div>
            )}

            {mode === 'expired' && (
              <div className="flex items-start gap-2 rounded-sm bg-amber-50 p-3 text-sm text-amber-900">
                <MailWarning className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>
                  <span className="font-medium">
                    {t('secure.login.expiredLink.title')}
                  </span>{' '}
                  {t('secure.login.expiredLink.message')}
                </span>
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
                {mode !== 'hint' && (
                  <FormField
                    control={form.control}
                    name="username"
                    rules={{
                      required: t('secure.login.errors.usernameRequired'),
                      validate: (value) =>
                        isValidUsername(value) ||
                        t('secure.login.errors.invalidUsername'),
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('secure.login.fieldNames.username')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            autoComplete="username"
                            ref={(element) => {
                              field.ref(element)
                              usernameRef.current = element
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="password"
                  rules={{
                    required: t('secure.login.errors.passwordRequired'),
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <HStack className="justify-between">
                        <FormLabel>
                          {t('secure.login.fieldNames.password')}
                        </FormLabel>
                        <Link
                          to="/secure/resetPassword"
                          className="hover:underline text-gray-500 text-sm"
                        >
                          {t('secure.login.buttons.forgotPassword')}
                        </Link>
                      </HStack>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            className="pr-12"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full mt-2 transform transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  loading={form.formState.isSubmitting}
                  loadingText={t('common.signingIn')}
                >
                  {t('secure.login.buttons.signIn')}
                </Button>
              </form>
            </Form>

            {/* A link left on a shared handset names whoever it was sent to, so
                the screen it opens must be leaveable without one. */}
            {mode === 'hint' && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={useDifferentAccount}
                  className="hover:underline text-gray-500 text-sm"
                >
                  {t('secure.login.hint.differentAccount')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
